// Bulk video export: ask for one, watch it run, read it back.
//
// The zip itself is never addressed from here — a download URL is minted by the
// getVideoExport callable, which is the only place the public/restricted
// decision is made. See docs/superpowers/specs/2026-09-07-bulk-video-export-design.md
import { httpsCallable } from 'firebase/functions'
import { collection, onSnapshot, orderBy, query, where } from 'firebase/firestore'
import { db, functions } from '../firebase'

/** Start an export, or re-share the matching one that already exists. */
export async function requestVideoExport({
  eventId, recipients, access, includeReels, force,
}) {
  const fn = httpsCallable(functions, 'requestVideoExport')
  const res = await fn({
    eventId,
    recipients,
    access,
    // Omitted rather than coerced: the callable falls back to the event
    // type's own default (reels in, for a wedding) when it is undefined.
    ...(includeReels === undefined ? {} : { includeReels }),
    force: force === true,
  })
  return res.data
}

/** What pressing Export would do right now — count, and any reusable export. */
export async function previewVideoExport(eventId, { includeReels } = {}) {
  const fn = httpsCallable(functions, 'previewVideoExport')
  // Omitted rather than sent as undefined when the caller has no opinion:
  // the callable then falls back to the event type's own default.
  const res = await fn(
    typeof includeReels === 'boolean' ? { eventId, includeReels } : { eventId },
  )
  return res.data
}

/** Add people to an existing export, and change its link access. No re-encode. */
export async function shareVideoExport({ exportId, recipients, access }) {
  const fn = httpsCallable(functions, 'shareVideoExport')
  const res = await fn({ exportId, recipients, access })
  return res.data
}

/** The event's exports, newest first. Staff-only, per the rules. */
export function subscribeEventExports(eventId, onChange) {
  const q = query(
    collection(db, 'videoExports'),
    where('eventId', '==', eventId),
    orderBy('createdAt', 'desc')
  )
  return onSnapshot(q, (snap) => {
    onChange(snap.docs
      // A deleted export leaves a tombstone behind so its recipients get an
      // explanation; the admin who deleted it wants the row gone.
      .filter((d) => d.data().status !== 'deleted')
      .map((d) => ({ id: d.id, ...d.data() })))
  })
}

/**
 * Live progress for one export.
 *
 * Progress is summed from the chunk documents rather than read from a counter
 * on the job: nothing increments a shared field, so a retried chunk corrects
 * itself instead of double-counting.
 */
export function subscribeExportProgress(exportId, onChange) {
  const q = collection(db, 'videoExports', exportId, 'chunks')
  return onSnapshot(q, (snap) => {
    let done = 0
    let failed = 0
    for (const d of snap.docs) {
      const data = d.data()
      done += (data.processed || []).length
      failed += (data.failed || []).length
    }
    onChange({ done, failed })
  })
}

export function exportPageUrl(exportId) {
  return `${window.location.origin}/exports/${exportId}`
}

/**
 * Resolve an export for the download page.
 *
 * Throws `functions/unauthenticated` when the export is restricted and nobody
 * is signed in — which the page turns into a sign-in form rather than an error.
 */
export async function fetchVideoExport(exportId) {
  const fn = httpsCallable(functions, 'getVideoExport')
  const res = await fn({ exportId })
  return res.data
}

/**
 * Destroy an export: its zips, its documents, and with them every signed URL
 * already in circulation. Sysadmins and the export's creator only — the
 * callable decides, this just asks.
 */
export async function deleteVideoExport(exportId) {
  const fn = httpsCallable(functions, 'deleteVideoExport')
  const res = await fn({ exportId })
  return res.data
}
